/**
 * کلاینتِ Supabase — به‌صورت تنبل ساخته می‌شه، نه موقعِ import.
 *
 * چرا تنبل: قبلاً همین بالای فایل createClient صدا زده می‌شد و از
 * AsyncStorage/Platform مستقیم می‌خوند. مشکلش: import همیشه قبل از کدِ
 * خودِ ماژول اجرا می‌شه، پس حتی اگه اپ اولین خط initCore() رو صدا بزنه،
 * اگه یه فایلِ دیگه زودتر `supabase` رو import کرده باشه، createClient با
 * پورت‌های خالی اجرا می‌شد. با Proxy، ساختِ واقعی تا اولین استفاده‌ی واقعی
 * (مثلاً `supabase.auth...`) عقب می‌افته — که همیشه بعد از initCore است.
 *
 * چرا هنوز اسم export یکیه (`supabase`): ده‌ها فایلِ ریپازیتوری همین‌جوری
 * `import { supabase } from '../lib/supabase'` می‌کنن و مستقیم `.from()`
 * صدا می‌زنن. عوض کردنِ الگو به یه factory یعنی همه‌ی اون فایل‌ها رو هم عوض
 * کنی؛ Proxy همون رابطِ قدیمی رو نگه می‌داره و فقط زمانِ ساختنش رو عقب می‌ندازه.
 *
 * پالیفیلِ URL این‌جا نیست: موتورِ جاواسکریپتِ RN (Hermes) قبلاً بهش نیاز
 * داشت، مرورگر ندارد. اپِ موبایل خودش `react-native-url-polyfill/auto` رو
 * توی بوت‌استرپش import می‌کنه (نگاه کن به native/bootstrap.ts) — قبل از
 * هر چیزی که به `supabase` نیاز داره.
 */
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { platformInfo, storage, supabaseCreds } from '../platform.ts';
import { queryShape } from './queryShape.ts';

let real: SupabaseClient | null = null;

function ensure(): SupabaseClient {
  if (real) return real;

  const { url: rawUrl, anonKey: rawKey } = supabaseCreds();
  const configured = !!rawUrl && /^https?:\/\//.test(rawUrl)
    && !!rawKey && rawKey !== 'SET_IN_ENV_OR_HERE';

  // با مقادیرِ جانشین: createClient روی URL نامعتبر throw می‌کنه و کلِ اپ
  // را قبل از رندر سفید می‌کنه — پس یه جانشینِ معتبر می‌ذاریم و پرچم بیرون می‌دیم.
  const url = configured ? rawUrl : 'https://placeholder.supabase.co';
  const anonKey = configured ? rawKey : 'public-anon-placeholder';
  const isWeb = platformInfo().isWeb;

  real = createClient(url, anonKey, {
    auth: {
      storage: isWeb ? undefined : toGoTrueStorage(),
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: isWeb,
    },
  });
  return real;
}

/** پورتِ storageِ core به شکلی که GoTrue (سوپابیس) انتظار داره. */
function toGoTrueStorage() {
  return {
    getItem: (k: string) => storage().getItem(k),
    setItem: (k: string, v: string) => storage().setItem(k, v),
    removeItem: (k: string) => storage().removeItem(k),
  };
}

/**
 * آیا کلیدهای واقعی موجودن. تابع است نه مقدارِ ثابت، چون قبل از initCore()
 * هنوز معلوم نیست — و بعدِ initCore هر بار خوندنش همون جوابِ درست رو می‌ده.
 */
export const isSupabaseConfigured = (): boolean => {
  const { url, anonKey } = supabaseCreds();
  return !!url && /^https?:\/\//.test(url) && !!anonKey && anonKey !== 'SET_IN_ENV_OR_HERE';
};

/* ─────────────────────── زمان‌گیریِ رفت‌وبرگشت‌ها ─────────────────────── */

/**
 * ناظرِ کوئری‌ها — «این کوئری این‌قدر طول کشید».
 *
 * چرا این‌جا: تنها جایی که **هر** رفت‌وبرگشتِ دیتابیس از آن رد می‌شود همین
 * فایل است. ده‌ها ریپازیتوری مستقیم `supabase.from(...)` صدا می‌زنند؛
 * اندازه‌گیری در هر کدام یعنی ده‌ها جا برای فراموش کردن.
 *
 * چرا **ناظر** و نه import کردنِ `lib/devlog`: آن ماژول خودش از همین کلاینت
 * استفاده می‌کند تا سطرهایش را بفرستد. import کردنِ دوطرفه یعنی یک حلقه‌ی
 * وابستگی برای چیزی که یک تابعِ ثبت‌شده حل می‌کند.
 *
 * وقتی ناظری ثبت نشده باشد، هیچ پوششی ساخته نمی‌شود و مسیر دقیقاً همان
 * چیزی است که قبلاً بود.
 */
export type QueryObserver = (
  table: string, op: string, ms: number, ok: boolean, detail: Record<string, unknown>,
  label?: string,
) => void;

let observer: QueryObserver | null = null;

export const observeQueries = (fn: QueryObserver | null): void => { observer = fn; };

/**
 * جای نگه‌داشتنِ برچسبِ صداکننده، روی خودِ شیءِ کوئری.
 *
 * چرا اصلاً لازم شد (WHOSUP-209): لاگ هر رفت‌وبرگشت را با
 * `${جدول}.${عمل}` نام می‌گذارد، پس **ده** کوئریِ کاملاً متفاوتِ صفحه‌ی کشف
 * همگی «events.select» می‌شدند. گزارشِ `npm run log:pull` می‌گفت «یک جایی
 * کند است» و هیچ‌وقت نمی‌گفت کجا — سه ثانیه‌ی این تیکت از خودِ لاگ
 * قابلِ ردیابی نبود.
 *
 * چرا با Symbol روی خودِ شیء و نه یک متغیرِ «کوئریِ جاری»: این درخواست‌ها
 * ده‌تایی و هم‌زمان می‌روند. هر متغیرِ مشترکی بینشان قاطی می‌شود و برچسبِ
 * یکی روی دیگری می‌نشیند — که از نبودنِ برچسب بدتر است.
 */
const LABEL_SLOT = Symbol.for('whosup.queryLabel');

interface LabelSlot { label?: string }

/**
 * «این کوئری مالِ این صداکننده است.»
 *
 * برچسب اولْ صداکننده را نام می‌برد و بعد زنجیره می‌آید، چون همان ترتیبی است
 * که آدم می‌خواند: `tagged('eventRepo.mine', supabase.from('events')…)`.
 *
 * ⚠ برچسب باید **ثابت** باشد — نه شناسه، نه فیلتر، نه شهر. همان قاعده‌ای که
 * `queryName` در `devlogRow.ts` دارد: این رشته کلیدِ گروه‌بندیِ گزارش است و
 * چیزی که از یک صدا به صدای بعد فرق کند، گزارش را به هزار سطرِ یک‌باره تبدیل
 * می‌کند.
 *
 * وقتی ناظری ثبت نشده باشد (یعنی لاگ خاموش است) هیچ جایگاهی ساخته نشده و
 * این تابع فقط همان شیء را دست‌نخورده برمی‌گرداند.
 */
export function tagged<T>(label: string, builder: T): T {
  const slot = (builder as Record<symbol, unknown> | null)?.[LABEL_SLOT] as
    LabelSlot | undefined;
  if (slot) slot.label = label;
  return builder;
}

/** عملیاتی که واقعاً یک درخواستِ شبکه می‌سازند. بقیه فقط فیلترند. */
const TIMED_OPS = new Set(['select', 'insert', 'update', 'upsert', 'delete']);

const clockNow = (): number => {
  const p = (globalThis as { performance?: { now?: () => number } }).performance;
  return p?.now ? p.now() : Date.now();
};

/**
 * سازنده‌ی کوئریِ سوپابیس یک Promise نیست، یک «then‌دار» است: تا `await`ش
 * نکنی درخواستی نمی‌رود. پس زمان‌گیری یعنی پیچیدنِ همان `then`.
 *
 * روی خودِ شیء نوشته می‌شود و نه با یک Proxy، چون متدهای زنجیره‌ای
 * (`.eq()`, `.single()`, `.order()`) همگی **همین شیء** را برمی‌گردانند؛ پس
 * پوششی که این‌جا گذاشته می‌شود تا آخرِ زنجیره سرِ جایش می‌ماند.
 */
function timeQuery<T>(builder: T, table: string, op: string): T {
  const fn = observer;
  if (!fn) return builder;
  const b = builder as unknown as {
    then: (onF?: (v: unknown) => unknown, onR?: (e: unknown) => unknown) => unknown;
  };
  if (typeof b?.then !== 'function') return builder;

  const t0 = clockNow();
  // جایگاهِ برچسب همین حالا ساخته می‌شود و تا آخرِ زنجیره روی همین شیء
  // می‌ماند؛ `tagged()` بعداً پُرش می‌کند. نگاه کن به LABEL_SLOT.
  const slot: LabelSlot = {};
  (b as unknown as Record<symbol, unknown>)[LABEL_SLOT] = slot;
  const original = b.then.bind(b);
  b.then = (onF, onR) => original(
    (res: unknown) => {
      const r = res as { error?: { message?: string; code?: string } | null; data?: unknown };
      const detail: Record<string, unknown> = {};
      if (Array.isArray(r?.data)) detail.rows = r.data.length;
      if (r?.error) { detail.error = r.error.message ?? ''; detail.code = r.error.code ?? ''; }
      Object.assign(detail, shapeOf(b, table));
      report(fn, table, op, t0, !r?.error, detail, slot.label);
      return onF ? onF(res) : res;
    },
    (e: unknown) => {
      report(
        fn, table, op, t0, false,
        { error: e instanceof Error ? e.message : String(e), ...shapeOf(b, table) }, slot.label,
      );
      if (onR) return onR(e);
      throw e;
    },
  );
  return builder;
}

/**
 * شکلِ درخواست (فیلترها، ستون‌ها، `limit`) از روی آدرسی که سازنده ساخته — در
 * لحظه‌ی پاسخ خوانده می‌شود، چون `.eq()`/`.limit()` بعد از `select()` اضافه
 * شده‌اند. مقدارِ هیچ فیلتری ثبت نمی‌شود؛ نگاه کن به `queryShape.ts`.
 */
function shapeOf(b: unknown, table: string): Record<string, unknown> {
  const q = b as { url?: unknown; body?: unknown } | null;
  return queryShape(q?.url, q?.body, table === 'rpc');
}

/** خودِ ثبت هیچ‌وقت نباید کوئریِ کاربر را بشکند. */
function report(
  fn: QueryObserver, table: string, op: string, t0: number,
  ok: boolean, detail: Record<string, unknown>, label?: string,
): void {
  try {
    fn(table, op, clockNow() - t0, ok, detail, label);
  } catch { /* لاگ که بترکد، داده‌ی کاربر نباید قربانی شود */ }
}

/** `from(...)` را می‌پیچد تا هرکدام از پنج عملیاتِ اصلی زمان‌دار برگردد. */
function timedTable(qb: object, table: string): object {
  return new Proxy(qb, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);
      if (typeof value !== 'function' || typeof prop !== 'string' || !TIMED_OPS.has(prop)) {
        return value;
      }
      return (...args: unknown[]) => timeQuery(
        (value as (...a: unknown[]) => unknown).apply(target, args), table, prop,
      );
    },
  });
}

/** رابطِ قدیمی: `supabase.from(...)`, `supabase.auth...` بدونِ هیچ تغییری در فایل‌های دیگه. */
export const supabase: SupabaseClient = new Proxy({} as SupabaseClient, {
  get(_target, prop, receiver) {
    const client = ensure();
    if (observer && (prop === 'from' || prop === 'rpc')) {
      const original = Reflect.get(client, prop, receiver) as (...a: unknown[]) => unknown;
      if (prop === 'from') {
        return (table: string, ...rest: unknown[]) => timedTable(
          original.call(client, table, ...rest) as object, table,
        );
      }
      // `rpc` خودش مستقیم یک then‌دار برمی‌گرداند — بدونِ مرحله‌ی `.select()`.
      return (name: string, ...rest: unknown[]) => timeQuery(
        original.call(client, name, ...rest), 'rpc', name,
      );
    }
    return Reflect.get(client, prop, receiver);
  },
});
